export const USER_ACCESS_MODES = {
  STANDARD: 'STANDARD',
  VENDOR: 'VENDOR',
  PRINT_ONLY: 'PRINT_ONLY',
  NONE: 'NONE'
};

const IGNORED_ACCESS_ROLES = ['Everyone'];

const normalizeGroups = (groups) => (Array.isArray(groups) ? groups : []);

const isPrintTagOnly = (groups) => {
  const meaningfulGroups = groups.filter((group) => !IGNORED_ACCESS_ROLES.includes(group));
  return meaningfulGroups.length > 0 && meaningfulGroups.every((group) => group === 'PRINT_TAG');
};

/**
 * Builds a centralized access profile from Auth0 UI group membership.
 *
 * Precedence: SUPER_ADMIN → STANDARD; only PRINT_TAG (Everyone ignored) → PRINT_ONLY;
 * VENDOR → VENDOR; other assigned roles → STANDARD; empty/invalid input → NONE.
 * PRINT_TAG combined with a non-vendor role keeps that menu and still grants Print Tag.
 * VENDOR never grants Print Tag, even when PRINT_TAG is also assigned.
 *
 * @param {unknown} groups - The signed-in user's UI group membership collection.
 * @returns {{
 *   mode: string,
 *   landingPath: string|null,
 *   canAccessPrintTag: boolean,
 *   canAccessAuxiliaryRoutes: boolean
 * }}
 */
export function createUserAccessProfile(groups) {
  const normalizedGroups = normalizeGroups(groups);

  if (normalizedGroups.length === 0) {
    return {
      mode: USER_ACCESS_MODES.NONE,
      landingPath: null,
      canAccessPrintTag: false,
      canAccessAuxiliaryRoutes: false
    };
  }

  if (normalizedGroups.includes('SUPER_ADMIN')) {
    return {
      mode: USER_ACCESS_MODES.STANDARD,
      landingPath: '/welcome',
      canAccessPrintTag: true,
      canAccessAuxiliaryRoutes: true
    };
  }

  if (isPrintTagOnly(normalizedGroups)) {
    return {
      mode: USER_ACCESS_MODES.PRINT_ONLY,
      landingPath: '/sales/print-tag',
      canAccessPrintTag: true,
      canAccessAuxiliaryRoutes: false
    };
  }

  const canAccessPrintTag = normalizedGroups.includes('PRINT_TAG');

  if (normalizedGroups.includes('VENDOR')) {
    return {
      mode: USER_ACCESS_MODES.VENDOR,
      landingPath: '/purchases/asn',
      canAccessPrintTag: false,
      canAccessAuxiliaryRoutes: true
    };
  }

  return {
    mode: USER_ACCESS_MODES.STANDARD,
    landingPath: '/welcome',
    canAccessPrintTag,
    canAccessAuxiliaryRoutes: true
  };
}

export default createUserAccessProfile;
